/**
 * EditGroupDialog Component
 * 
 * Dialog for editing an existing account group.
 */

import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UseAccountSettingsUIStateReturn } from "../hooks/useAccountSettingsUIState";
import { UseAccountSettingsHandlersReturn } from "../hooks/useAccountSettingsHandlers";

export interface EditGroupDialogProps {
  open: boolean;
  onClose: () => void;
  uiState: UseAccountSettingsUIStateReturn;
  handlers: UseAccountSettingsHandlersReturn;
}

/**
 * Edit group dialog component
 */
export default function EditGroupDialog({
  open,
  onClose,
  uiState,
  handlers,
}: EditGroupDialogProps): React.ReactElement {
  const handleClose = (): void => {
    uiState.setEditDialogOpen(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Edit Account Group</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Group Name"
            value={uiState.editGroupName}
            onChange={(e) => uiState.setEditGroupName(e.target.value)}
            fullWidth
            required
            autoFocus
          />
          {uiState.editingGroup && uiState.editGroupName !== uiState.editingGroup.name && (
            <Typography variant="caption" color="warning.main">
              Note: Renaming this group will update all accounts using it.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlers.handleUpdateGroup}
          disabled={!uiState.editGroupName.trim()}
          sx={{ flex: 1, py: 1.5 }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
